/**
 * The class session a Jarvis story attaches to: the most recent session the
 * student was physically at, at or before the save.
 *
 * PRESENT and TARDY count on the student's own session. MAKEUP_PRESENT and
 * MAKEUP_TARDY are written on the session they missed, so those count the
 * makeup's destination instead. ENROLLED does not count — attendance that
 * has not been taken yet is not an attended session. A session already
 * marked attended counts even when its start is still ahead of the save.
 */
import dayjs from "dayjs"

import { ATTENDANCE_STATUSES } from "#constants/globalConstants"

const OWN_ATTENDED = new Set<string>([
  ATTENDANCE_STATUSES.PRESENT,
  ATTENDANCE_STATUSES.TARDY,
])

const MAKEUP_ATTENDED = new Set<string>([
  ATTENDANCE_STATUSES.MAKEUP_PRESENT,
  ATTENDANCE_STATUSES.MAKEUP_TARDY,
])

export type AttendanceLike =
  | Record<string, { status?: string; sessionId?: string }[] | undefined>
  | null
  | undefined

export type MakeupDestination = {
  missedSessionId: string
  makeupSessionId?: string | null
}

export const makeupAttendedMissedSessionIds = (
  attendance: AttendanceLike
): string[] => {
  const ids: string[] = []
  for (const cells of Object.values(attendance ?? {})) {
    for (const cell of cells ?? []) {
      if (!cell?.sessionId || !cell.status) continue
      if (!MAKEUP_ATTENDED.has(cell.status)) continue
      ids.push(cell.sessionId)
    }
  }
  return ids
}

export const attendedSessionIds = (
  attendance: AttendanceLike,
  makeups: readonly MakeupDestination[]
): string[] => {
  const ids = new Set<string>()
  for (const cells of Object.values(attendance ?? {})) {
    for (const cell of cells ?? []) {
      if (!cell?.sessionId || !cell.status) continue
      if (OWN_ATTENDED.has(cell.status)) {
        ids.add(cell.sessionId)
        continue
      }
      if (!MAKEUP_ATTENDED.has(cell.status)) continue
      const destination = makeups.find(
        (makeup) =>
          makeup.missedSessionId === cell.sessionId && makeup.makeupSessionId
      )?.makeupSessionId
      if (destination) ids.add(destination)
    }
  }
  return [...ids]
}

/**
 * Attended session closest to the save. Distance is |eventStart − createdAt|,
 * so a class already marked present still wins if the teacher saves before
 * the official start. An equal distance keeps the earlier start, then the
 * earlier session id.
 */
export const nearestAttendedSessionId = (
  sessions: readonly { id: string; eventStart?: string | null }[],
  attendedIds: ReadonlySet<string>,
  createdAtIso: string
): string | undefined => {
  const createdAt = dayjs(createdAtIso)
  if (!createdAt.isValid()) return undefined
  const createdMs = createdAt.valueOf()

  let bestId: string | undefined
  let bestDistance = Number.POSITIVE_INFINITY
  let bestStart = Number.POSITIVE_INFINITY

  for (const session of sessions) {
    if (!attendedIds.has(session.id) || !session.eventStart) continue
    const start = dayjs(session.eventStart)
    if (!start.isValid()) continue
    const startMs = start.valueOf()
    const distance = Math.abs(startMs - createdMs)
    const closer = distance < bestDistance
    const sameDistanceEarlierStart =
      distance === bestDistance && startMs < bestStart
    const sameMomentEarlierId =
      distance === bestDistance &&
      startMs === bestStart &&
      (bestId === undefined || session.id < bestId)
    if (!closer && !sameDistanceEarlierStart && !sameMomentEarlierId) continue
    bestDistance = distance
    bestStart = startMs
    bestId = session.id
  }

  return bestId
}

export const noAttendedSessionMessage = (name: string): string => {
  const first = name.trim().split(/\s+/)[0]
  const who = first || "This student"
  return `${who} hasn't attended a class yet, so this story can't be saved`
}
